import { createSelector } from '@reduxjs/toolkit';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { format } from 'date-fns';
import { LoaderCircleIcon } from 'lucide-preact';
import { useMemo, useState } from 'preact/compat';

import { Button } from '@/components/Buttons';
import Loader from '@/components/Loader';
import { handlePatchUserProductSuccess } from '@/components/products/utils';
import { useLatestActiveTime } from '@/hooks/useLatestActiveDateTime';
import { getProductsQueryOptions, patchUserProductMutationFn } from '@/queries/products';
import { ParsedUserProduct, Product } from '@/queries/products/types';
import { AppState, useSelector } from '@/redux/helper';

import ProductTag from './ProductTag';
import UpsellPromotedProductSection from './UpsellPromotedProductSection';
import useResolveUpsellProduct from './useResolveUpsellProduct';
import UserProductImage from './UserProductImage';
import {
  DETAILS_SECTION_CLASS,
  getProductDescription,
  getProductMonthlyPrice,
  getProductPrice,
  getProductTitle,
  getSponsorshipTierLookupKey,
  getSwitchableProduct,
  habitatIdsOf,
  selectProductsMap,
} from './utils';

interface SponsorshipProductDetailsProps {
  userProduct: ParsedUserProduct;
  activeUserProducts: ParsedUserProduct[];
}

const SponsorshipProductDetails = ({ userProduct, activeUserProducts }: SponsorshipProductDetailsProps) => {
  const [processingButtonId, setProcessingButtonId] = useState<string | null>(null);
  const lookupKey = getSponsorshipTierLookupKey(userProduct.product.productLookupKey);
  const [now] = useLatestActiveTime();

  const habitatId = habitatIdsOf(userProduct)[0];
  const selectHabitat = useMemo(
    () =>
      createSelector(
        (state: AppState) => state.allHabitats,
        (allHabitats) => {
          if (!habitatId) return undefined;
          const habitat = allHabitats.find((h) => h._id === habitatId);
          return habitat
            ? {
                imageURL: habitat.profileImage,
                title: habitat.animal,
              }
            : undefined;
        },
      ),
    [habitatId],
  );
  const habitat = useSelector(selectHabitat);
  const upsellType = useResolveUpsellProduct(userProduct, activeUserProducts);

  const { data: productsMap, isLoading } = useQuery({
    ...getProductsQueryOptions({ lookupKey }),
    enabled: !!lookupKey,
    select: selectProductsMap,
  });

  const queryClient = useQueryClient();
  const { mutate: patchUserProduct, isPending } = useMutation({
    mutationFn: patchUserProductMutationFn,
    onSuccess: handlePatchUserProductSuccess(queryClient),
    onSettled: () => {
      setProcessingButtonId(null);
    },
  });

  const currentProduct = productsMap?.get(userProduct.product.productLookupKey ?? '');

  if (isLoading) {
    return (
      <div className="flex min-h-64 items-center justify-center">
        <Loader width="64px" height="64px" />
      </div>
    );
  }

  if (!currentProduct) {
    return <div className="flex min-h-64 items-center justify-center">Unknown product</div>;
  }

  const switchableProduct = getSwitchableProduct(userProduct.product.productLookupKey, productsMap);

  const handleResume = () => {
    patchUserProduct({
      userProductId: userProduct._id,
      action: 'resume',
    });
  };

  const handleTierChange = (product: Product) => {
    patchUserProduct({
      userProductId: userProduct._id,
      action: 'tierChange',
      targetProductId: product._id,
      targetPriceId: product.price.priceId,
    });
  };

  const handleCancel = () => {
    patchUserProduct({
      userProductId: userProduct._id,
      action: 'cancel',
      cancellationReason: 'User cancelled',
    });
  };

  const currentProductPrice = getProductPrice(userProduct.product.price);
  const currentProductDescription = getProductDescription(userProduct, now);
  const switchableProductMonthlyPrice = getProductMonthlyPrice(switchableProduct?.price);

  const isSwitchingProduct = userProduct.scheduledProductId !== userProduct.productId;
  const isSwitchingToSwitchableProduct = userProduct.scheduledProductId === switchableProduct?._id;

  return (
    <div>
      <div className={clsx(DETAILS_SECTION_CLASS)}>
        <div className="preflight flex items-center gap-3 py-5 mobile:flex-col mobile:px-3">
          <div className="flex min-w-0 items-center gap-3 mobile:w-full desktop:grow">
            <UserProductImage
              className="size-11 shrink-0 desktop:size-[60px]"
              imageURL={habitat?.imageURL}
              title={habitat?.title}
            />
            <div className="min-w-0 mobile:grow">
              <h6 className="preflight preflight-h6 line-clamp-2 text-lg font-bold text-neutral-600 desktop:line-clamp-1 desktop:text-2xl">
                {getProductTitle(currentProduct.productLookupKey, currentProduct.name, habitat?.title)}
              </h6>
              <div className="flex justify-between gap-2 text-base text-stone-500 mobile:hidden">
                <p className="preflight preflight-p line-clamp-1 desktop:text-xl">{currentProductDescription}</p>
                <div className="desktop:hidden">{currentProductPrice}</div>
              </div>
            </div>
          </div>
          <div className="flex w-full justify-between gap-2 text-base text-stone-500 desktop:hidden">
            <p className="preflight preflight-p line-clamp-1 desktop:text-xl">{currentProductDescription}</p>
            <div className="desktop:hidden">{currentProductPrice}</div>
          </div>
          <div className="flex shrink-0 items-center gap-6 mobile:w-full">
            <div className="text-lg text-stone-500 mobile:hidden">{currentProductPrice}</div>
            <Button
              data-id="current-product-button"
              variant={userProduct.cancellationInfo ? 'default' : 'outline'}
              disabled={isPending}
              type="button"
              className="min-w-40 shrink-0 px-5 py-3 text-base/none font-bold mobile:w-full"
              onClick={() => {
                setProcessingButtonId('current-product-button');
                if (isSwitchingProduct) {
                  handleResume();
                } else {
                  handleCancel();
                }
              }}
            >
              {isPending && processingButtonId === 'current-product-button' ? (
                <LoaderCircleIcon className="size-4 animate-spin" />
              ) : isSwitchingProduct ? (
                'Resume'
              ) : (
                'Cancel'
              )}
            </Button>
          </div>
        </div>
        {switchableProduct && (
          <div className="preflight py-5 mobile:px-3">
            <div className="flex items-center gap-3 mobile:flex-col">
              <div className="flex min-w-0 items-center gap-3 mobile:w-full desktop:grow">
                <UserProductImage
                  className="size-11 shrink-0 desktop:size-[60px]"
                  imageURL={habitat?.imageURL}
                  title={habitat?.title}
                />
                <div>
                  <h6 className="preflight preflight-h6 line-clamp-2 text-lg font-bold text-neutral-600 desktop:line-clamp-1 desktop:text-2xl">
                    {getProductTitle(switchableProduct.productLookupKey, switchableProduct.name, habitat?.title)}
                  </h6>
                  <p className="preflight preflight-p line-clamp-1 text-xl text-stone-500 mobile:hidden">
                    {switchableProductMonthlyPrice}
                  </p>
                </div>
              </div>
              {(switchableProduct.price.interval !== 'year' || switchableProduct.price.metadata?.tag) && (
                <p className="preflight preflight-p line-clamp-1 w-full text-base text-stone-500 desktop:hidden">
                  {switchableProductMonthlyPrice}
                </p>
              )}
              {switchableProduct.price.interval === 'year' && (
                <div className="flex shrink-0 items-center gap-2 mobile:w-full desktop:mr-3">
                  {switchableProduct.price.metadata?.tag && <ProductTag>{switchableProduct.price.metadata.tag}</ProductTag>}
                  <div className="text-base text-stone-500 desktop:text-lg">{getProductPrice(switchableProduct.price)}</div>
                </div>
              )}
              <div className="mobile:w-full">
                <Button
                  data-id="switchable-product-button"
                  variant={isSwitchingToSwitchableProduct ? 'outline' : 'default'}
                  disabled={isPending}
                  type="button"
                  className="min-w-40 shrink-0 px-5 py-3 text-base/none font-bold mobile:w-full"
                  onClick={() => {
                    setProcessingButtonId('switchable-product-button');
                    if (isSwitchingToSwitchableProduct) {
                      handleResume();
                    } else {
                      handleTierChange(switchableProduct);
                    }
                  }}
                >
                  {isPending && processingButtonId === 'switchable-product-button' ? (
                    <LoaderCircleIcon className="size-4 animate-spin" />
                  ) : isSwitchingToSwitchableProduct ? (
                    'Cancel Switch'
                  ) : (
                    `Switch to ${switchableProduct.price.interval === 'year' ? 'Yearly' : 'Monthly'} Billing`
                  )}
                </Button>
              </div>
            </div>
            <p className="preflight preflight-p mt-4 text-base text-stone-500 desktop:text-lg">
              By switching, you will pay on {format(userProduct.validUntil, 'MMMM d yyyy')}, after your current plan expires
            </p>
          </div>
        )}
      </div>
      {upsellType && (
        <UpsellPromotedProductSection
          upsellType={upsellType}
          habitatId={habitatId}
          activeUserProducts={activeUserProducts}
        />
      )}
    </div>
  );
};

export default SponsorshipProductDetails;
